import { getColumnInfo } from '../analyticsDataSlice';
import { TpDataContext, TpDataTable, TpDataTableColumn, TpDendrogram, TpDendrogramLink, VALTYPES } from '../interfaces';

export function pad(num: any, size: any): any {
  num = num.toString();
  while (num.length < size) num = '0' + num;
  return num;
}

export function loadDendrogram(
  id: string,
  name: string,
  description: string,
  canonicalTypeId: string,
  dendro_str: string,
): TpDendrogram {
  const links: TpDendrogramLink[] = [];
  const lines = dendro_str.split(/\r?\n/);
  const itemId2PosIndexMap: { [id: string]: number } = {};
  const linksMap: { [id: string]: TpDendrogramLink } = {};
  let maxDistance = 0;
  lines.shift();
  for (const line of lines) {
    if (line.length > 0) {
      const cells = line.split('\t');
      const idIn1 = cells[0];
      const idIn2 = cells[1];
      const link = {
        idIn1,
        idIn2,
        idOut: cells[2],
        distance: parseFloat(cells[3]),
      };
      links.push(link);
      linksMap[link.idOut] = link;
      maxDistance = Math.max(maxDistance, link.distance);
    }
  }

  let curIndex = 0;

  function processTreeLink(outId: string, distance?: number): any {
    const link = linksMap[outId];
    if (!link) {
      itemId2PosIndexMap[outId] = curIndex;
      curIndex++;
      return {
        itemId: outId,
        distance: 0,
        index: curIndex - 0.5,
      };
    }
    const leaf1 = processTreeLink(link.idIn1);
    const leaf2 = processTreeLink(link.idIn2);
    return {
      leaf1,
      leaf2,
      distance: distance || link.distance,
      index: (leaf1.index + leaf2.index) / 2,
    };
  }

  const tree = processTreeLink(links[links.length - 1].idOut, maxDistance);

  return {
    id,
    name,
    description,
    canonicalTypeId,
    links,
    itemId2PosIndexMap,
    tree,
  };
}

export function createItemIdMap(itemIdList: string[]) {
  const rs: { [id: string]: number } = {};
  let idx = 0;
  for (const itemId of itemIdList) {
    rs[itemId] = idx;
    idx++;
  }
  return rs;
}

export function parseCsv(
  sourceTxt: string,
  splitbyPipe: boolean,
  id: string,
  rowDataType: string,
  colItemId: string,
  colLabel: string,
): TpDataTable {
  const lines = sourceTxt.split(/\r?\n/);
  const rows = [];
  for (const line of lines) {
    if (line.length > 0) {
      const cells = splitbyPipe ? line.split(/\|/) : line.split(/,/);
      rows.push(cells);
    }
  }
  const columnIds: string[] = rows.shift() || [];
  const rowCount = rows.length;
  const columnData: any = {};
  let colIdx = 0;
  const columns: any[] = [];
  for (const headerId of columnIds) {
    const col = [];
    for (let i = 0; i < rowCount; i++) {
      const val: any = rows[i][colIdx];
      col.push(val);
    }
    columnData[headerId] = col;
    let valueType = VALTYPES.CATEGORICAL;
    if (headerId == 'asv_id') valueType = VALTYPES.STRING;
    columns.push({
      id: headerId,
      name: headerId,
      valueType,
      augmentedData: {},
      decimalDigits: 3,
    });
    colIdx += 1;
  }

  return {
    columns,
    columnData,
    columnGroups: [],
    id,
    name: id,
    canonicalTypeId: rowDataType,
    colItemId,
    colLabel,
    itemIdMap: createItemIdMap(columnData[colItemId]),
  };
}

export function JSON2DataTable(
  data: any,
  id: string,
  rowDataType: string,
  colItemId: string,
  colLabel: string,
): TpDataTable {
  const columnData: any = {};
  const columns: any[] = [];

  for (const colId in data) {
    columns.push({
      id: colId,
      name: colId,
      valueType: VALTYPES.CATEGORICAL,
      augmentedData: {},
      decimalDigits: 3,
    });
    columnData[colId] = data[colId];
  }

  return {
    columns,
    columnData,
    columnGroups: [],
    id,
    name: id,
    canonicalTypeId: rowDataType,
    colItemId,
    colLabel,
    itemIdMap: createItemIdMap(columnData[colItemId]),
  };
}

export function setColumName(table: TpDataTable, colId: string, name: string) {
  getColumnInfo(table, colId).name = name;
}

export function createColumnGroup(table: TpDataTable, groupId: string, groupName: string, colIds: string[]) {
  table.columnGroups.push({ id: groupId, name: groupName });
  for (const colId of colIds) {
    getColumnInfo(table, colId).groupId = groupId;
  }
}

function convertStringToNumerical(columnData: string[]) {
  return columnData.map((val: any) => parseFloat(val));
}

function convertStringToCategorical(columnData: string[]) {
  return columnData.map((val: any) => {
    if (!val || val == 'null') return [];
    return val.toString().split(';');
  });
}

export function makeNumericalColumn(table: TpDataTable, colId: string, decimalDigits?: number) {
  const colInfo = getColumnInfo(table, colId);
  colInfo.valueType = VALTYPES.NUMBER;
  if (decimalDigits !== undefined) colInfo.decimalDigits = decimalDigits;
  table.columnData[colId] = convertStringToNumerical(table.columnData[colId]);
}

export function makeIntegerColumn(table: TpDataTable, colId: string) {
  const colInfo = getColumnInfo(table, colId);
  colInfo.valueType = VALTYPES.INTEGER;
  table.columnData[colId] = convertStringToNumerical(table.columnData[colId]);
}

export function makeStringColumn(table: TpDataTable, colId: string) {
  getColumnInfo(table, colId).valueType = VALTYPES.STRING;
}

export function makeMultiCategoricalColumn(table: TpDataTable, colId: string) {
  getColumnInfo(table, colId).valueType = VALTYPES.MULTICAT;
  table.columnData[colId] = convertStringToCategorical(table.columnData[colId]);
}

export function castSingleColumn(columnData: string[], valueType: string) {
  switch (valueType) {
    case VALTYPES.STRING:
      return columnData;
    case VALTYPES.INTEGER:
      return convertStringToNumerical(columnData);
    case VALTYPES.NUMBER:
      return convertStringToNumerical(columnData);
    case VALTYPES.MULTISTRING:
      return convertStringToCategorical(columnData);
    case VALTYPES.MULTICAT:
      return convertStringToCategorical(columnData);
    default:
      return columnData;
  }
}

export function convertCategoricalColumn(
  dataContext: TpDataContext,
  dataTable: TpDataTable,
  colInfo: TpDataTableColumn,
) {
  if (colInfo.valueType == VALTYPES.CATEGORICAL) {
    const dataOrig = dataTable.columnData[colInfo.id];
    const catMapper: { [key: string]: number } = {};
    const cats: string[] = [];
    const dataConv: number[] = [];
    for (const val of dataOrig) {
      if (catMapper[val] === undefined) {
        cats.push(val);
        catMapper[val] = cats.length - 1;
      }
      dataConv.push(catMapper[val]);
    }
    dataTable.columnData[colInfo.id] = /*new Int16Array*/ dataConv;
    colInfo.catConvertor = cats;
    colInfo;
  }

  if (colInfo.valueType == VALTYPES.MULTICAT) {
    const dataOrig = dataTable.columnData[colInfo.id];
    const catMapper: { [key: string]: number } = {};
    const cats: string[] = [];
    const dataConv: number[][] = [];
    for (const cell of dataOrig) {
      const cellConv: number[] = [];
      for (const val of cell) {
        if (catMapper[val] === undefined) {
          cats.push(val);
          catMapper[val] = cats.length - 1;
        }
        cellConv.push(catMapper[val]);
      }
      dataConv.push(cellConv);
    }
    dataTable.columnData[colInfo.id] = dataConv;
    colInfo.catConvertor = cats;
  }

  if (colInfo.catConvertor) {
    colInfo.catConvertorInv = {};
    for (const [catIdx, catName] of colInfo.catConvertor.entries()) colInfo.catConvertorInv[catName] = catIdx;
  }
}

export function convertAllCategoricalColumns(dataContext: TpDataContext) {
  for (const dataTable of dataContext.dataTables) {
    for (const colInfo of dataTable.columns) {
      convertCategoricalColumn(dataContext, dataTable, colInfo);
    }
  }
}
